// indicators.tsx — the indicator catalog and the quick bar (docs/INDICATORS_PARITY_PLAN.md §2).
// Pure UI: the app owns what is on and what is pinned and hands in `items`.
import { useEffect, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import { IconButton, SearchField, Toggle } from './components';
import { SettingsGroupTitle } from './settings';
import { type IconProps, IconSettings, IconStar, IconStarFilled } from './icons';
import { tr } from './i18n';

export interface IndicatorItem {
  type: string;
  name: string;
  group: string;
  icon: ComponentType<IconProps>;
  help?: string;
  on: boolean;
  /** Has a settings popover — the gear is shown. */
  configurable: boolean;
}

interface CatalogProps {
  items: IndicatorItem[];
  quick: string[];
  onToggle(type: string): void;
  onPin(type: string, pinned: boolean): void;
  onSettings(type: string): void;
  onClose(): void;
  style?: CSSProperties;
}

/** The rows, grouped in order of first appearance. Hook-free so a test can walk it without a DOM. */
export function IndicatorList({ items, quick, query, onToggle, onPin, onSettings }: Omit<CatalogProps, 'onClose' | 'style'> & { query: string }) {
  const q = query.trim().toLowerCase();
  const shown = items.filter((i) => !q || i.name.toLowerCase().includes(q));
  const groups = [...new Set(shown.map((i) => i.group))];
  if (!shown.length) return <div style={{ color: 'var(--low)', padding: 8 }}>{tr('No matches')}</div>;
  return (
    <>
      {groups.map((g) => (
        <div key={g}>
          <SettingsGroupTitle>{g}</SettingsGroupTitle>
          {shown.filter((i) => i.group === g).map((it) => {
            const pinned = quick.includes(it.type);
            const Icon = it.icon;
            const Star = pinned ? IconStarFilled : IconStar;
            return (
              <div key={it.type} className="qt-ind__row" data-on={it.on} data-tip={it.help}>
                <span className="qt-ind__ico"><Icon size={16} /></span>
                <span className="qt-ind__name">{it.name}</span>
                <IconButton data-pin={it.type} label={pinned ? tr('Unpin from quick bar') : tr('Pin to quick bar')} active={pinned} onClick={() => onPin(it.type, !pinned)}><Star size={14} /></IconButton>
                <Toggle checked={it.on} onChange={() => onToggle(it.type)} label={it.name} />
                {it.configurable
                  ? <IconButton label={tr('Indicator settings')} onClick={() => onSettings(it.type)}><IconSettings size={14} /></IconButton>
                  : <span className="qt-ind__gear-slot" />}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

export function IndicatorCatalog(p: CatalogProps) {
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const { onClose } = p;

  useEffect(() => {
    // The parent also holds the opening button: counting it as inside keeps the
    // button's own click from re-opening the menu it just closed.
    const onDown = (e: PointerEvent) => {
      const host = boxRef.current?.parentElement ?? boxRef.current;
      if (host && !host.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '/' && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        boxRef.current?.querySelector('input')?.focus();
      }
    };
    // capture: the chart canvases swallow bubbling pointer events (zoom/pan, drawing layers)
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  return (
    <div ref={boxRef} className="qt-menu qt-ind" style={p.style}>
      <SearchField value={q} onChange={setQ} placeholder={tr('Search indicators…')} hotkey="/" />
      <IndicatorList {...p} query={q} />
    </div>
  );
}

export function IndicatorQuickBar({ items, quick, onToggle, onSettings, onOpenCatalog }: Pick<CatalogProps, 'items' | 'quick' | 'onToggle' | 'onSettings'> & { onOpenCatalog(): void }) {
  return (
    <div className="qt-quickbar">
      {quick.map((t) => items.find((i) => i.type === t)).filter((i): i is IndicatorItem => !!i).map((it) => {
        const Icon = it.icon;
        return (
          <IconButton
            key={it.type}
            label={it.name}
            active={it.on}
            onClick={() => onToggle(it.type)}
            onContextMenu={(e) => { if (it.configurable) { e.preventDefault(); onSettings(it.type); } }}
          ><Icon size={16} /></IconButton>
        );
      })}
      <IconButton label={tr('Add indicator')} onClick={onOpenCatalog}>+</IconButton>
    </div>
  );
}
